import emailjs from '@emailjs/browser';

class VisitorTracker {
  constructor() {
    this.hasTracked = false;
    this.visitStartTime = Date.now();
  }

  generateSessionId() {
    return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  }

  async getVisitorInfo() {
    const info = {
      // Basic visit info
      timestamp: new Date().toLocaleString(),
      utcTimestamp: new Date().toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      sessionId: this.generateSessionId(),
      
      // Language & locale
      language: navigator.language,
      languages: navigator.languages?.join(', ') || 'N/A',
      
      // Device & browser
      platform: navigator.platform,
      userAgent: navigator.userAgent,
      cookieEnabled: navigator.cookieEnabled,
      onlineStatus: navigator.onLine ? 'Online' : 'Offline',
      
      // Screen & display
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      availableScreenSize: `${window.screen.availWidth}x${window.screen.availHeight}`,
      windowSize: `${window.innerWidth}x${window.innerHeight}`,
      colorDepth: `${window.screen.colorDepth}-bit`,
      pixelRatio: window.devicePixelRatio || 1,
      screenOrientation: window.screen.orientation?.type || 'N/A',
      
      // Navigation & page info
      referrer: document.referrer || 'Direct visit',
      currentUrl: window.location.href,
      
      // Performance & connection
      connectionType: navigator.connection?.effectiveType || 'N/A',
      connectionDownlink: navigator.connection?.downlink || 'N/A',
      
      // Time on site (will be 0 for initial visit)
      timeOnSite: Math.round((Date.now() - performance.timing.navigationStart) / 1000),
      loadTime: Math.round(performance.timing.loadEventEnd - performance.timing.navigationStart),
    };

    // Skip geolocation to avoid suspicious permission prompts
    info.location = 'Not collected (privacy-friendly)';

    // Try to get IP and additional info from external API
    try {
      const response = await fetch('https://ipapi.co/json/');
      const ipData = await response.json();
      info.ip = ipData.ip || 'N/A';
      info.city = ipData.city || 'N/A';
      info.region = ipData.region || 'N/A';
      info.country = ipData.country_name || 'N/A';
      info.countryCode = ipData.country_code || 'N/A';
      info.isp = ipData.org || 'N/A';
      info.timezone = ipData.timezone || info.timezone;
    } catch (error) {
      info.ip = 'Unable to fetch';
      info.city = 'N/A';
      info.region = 'N/A';
      info.country = 'N/A';
      info.isp = 'N/A';
    }

    // Device type detection
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isTablet = /iPad|Android(?!.*Mobile)/i.test(navigator.userAgent);
    info.deviceType = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop';

    // Browser detection
    const getBrowser = () => {
      const ua = navigator.userAgent;
      if (ua.includes('Chrome')) return 'Chrome';
      if (ua.includes('Firefox')) return 'Firefox';
      if (ua.includes('Safari')) return 'Safari';
      if (ua.includes('Edge')) return 'Edge';
      if (ua.includes('Opera')) return 'Opera';
      return 'Unknown';
    };
    info.browser = getBrowser();

    // OS detection
    const getOS = () => {
      const ua = navigator.userAgent;
      if (ua.includes('Windows')) return 'Windows';
      if (ua.includes('Mac')) return 'macOS';
      if (ua.includes('Linux')) return 'Linux';
      if (ua.includes('Android')) return 'Android';
      if (ua.includes('iOS')) return 'iOS';
      return 'Unknown';
    };
    info.operatingSystem = getOS();

    return info;
  }

  formatEmailMessage(visitorInfo) {
    return `
🎉 NEW VISITOR ALERT! 🎉

Someone just visited your amazing portfolio! Here's their info:

📅 VISIT DETAILS:
• Timestamp: ${visitorInfo.timestamp}
• Timezone: ${visitorInfo.timezone}
• Current URL: ${visitorInfo.currentUrl}
• Referrer: ${visitorInfo.referrer}

🌍 LOCATION INFO:
• IP Address: ${visitorInfo.ip}
• City: ${visitorInfo.city}
• Region: ${visitorInfo.region}
• Country: ${visitorInfo.country} (${visitorInfo.countryCode})
• ISP: ${visitorInfo.isp}
• GPS Coordinates: ${visitorInfo.location}

💻 DEVICE & BROWSER:
• Device Type: ${visitorInfo.deviceType}
• Operating System: ${visitorInfo.operatingSystem}
• Browser: ${visitorInfo.browser}
• Screen Resolution: ${visitorInfo.screenResolution}
• Available Screen: ${visitorInfo.availableScreenSize}
• Window Size: ${visitorInfo.windowSize}
• Color Depth: ${visitorInfo.colorDepth}
• Pixel Ratio: ${visitorInfo.pixelRatio}
• Screen Orientation: ${visitorInfo.screenOrientation}

🌐 USER PREFERENCES:
• Language: ${visitorInfo.language}
• All Languages: ${visitorInfo.languages}
• Cookies Enabled: ${visitorInfo.cookieEnabled}
• Online Status: ${visitorInfo.onlineStatus}

📡 CONNECTION INFO:
• Connection Type: ${visitorInfo.connectionType}
• Download Speed: ${visitorInfo.connectionDownlink}Mbps

⏱️ PERFORMANCE:
• Time on Site: ${visitorInfo.timeOnSite} seconds
• Page Load Time: ${visitorInfo.loadTime}ms
• Session ID: ${visitorInfo.sessionId}

🔧 BROWSER DETAILS:
User Agent: ${visitorInfo.userAgent}

---
This email was automatically generated by Hammad's Portfolio Analytics Bot 🤖
    `.trim();
  }

  async trackVisitor() {
    // Only track once per session
    if (this.hasTracked || sessionStorage.getItem('visitor_tracked')) {
      return;
    }

    try {
      console.log('🔍 Gathering visitor information...');
      const visitorInfo = await this.getVisitorInfo();
      
      const emailData = {
        from_name: "Hammad's Analytics Bot",
        from_email: "automated-email@hammadahmed.me",
        subject: "🚨 Someone just opened your website!",
        message: this.formatEmailMessage(visitorInfo)
      };

      // Send email using the same EmailJS service
      await emailjs.send(
        'service_tq7rqy1', 
        'template_lzus3ep', 
        emailData, 
        'DdicUw29yGs6HtHSS'
      );

      console.log('📧 Visitor analytics sent successfully!');
      this.hasTracked = true;
      sessionStorage.setItem('visitor_tracked', 'true');
      
    } catch (error) {
      console.error('❌ Failed to send visitor analytics:', error);
    }
  }
}

export default new VisitorTracker();
